import React, { useState, useCallback, useEffect, FunctionComponent, useMemo } from "react";
import fetch from "lib/fetch";
import { format } from "lib/globalize-util";
import { Form } from "formsy-react";
import ConfirmDialog from "components/dialog/confirm";
import { CompanySelect } from "components/cart/company-select";
import type { IScope } from "reducers/scope";
import { TFDMCompanyRecord, CompanyRecord } from "records/company/company";
import { IUser } from "containers/management/management-types";
import { IOrder } from "records/order";
import { makeStyles } from "@material-ui/styles";
import { rawTheme } from "styles/theme";
import { DATATEST } from "constants/integration";

interface IProps {
    open: boolean;
    onClose: (company: TFDMCompanyRecord) => void;
    onConfirm: (company: TFDMCompanyRecord) => void;
    onFetchStartOrderCompanies: (params) => void;
    onSnackbarAdd: (param: { message: string } | any) => void;
    onSyncFromCart: (order: IOrder, meta: { syncToCart?: boolean }) => void;
    fetchingCompanies: boolean;
    companies: TFDMCompanyRecord[];
    companyIds: string[];
    hasMoreCompanies: boolean;
    orderId: string;
    user: IUser;
    scope: IScope;
}

export const CompanySelectionModal: FunctionComponent<IProps> = ({
    open,
    onClose,
    onConfirm,
    onFetchStartOrderCompanies,
    onSnackbarAdd,
    onSyncFromCart,
    fetchingCompanies,
    companies,
    companyIds,
    hasMoreCompanies,
    orderId,
    user,
    scope,
}) => {
    const hasDiscountPreference = scope?.brand?.hasPreference("discount_allowed");

    const [selectedCompany, setSelectedCompany] = useState<TFDMCompanyRecord | undefined>();
    const styles = useMemo(
        () => ({
            actionsStyle: {
                padding: "40px 24px 24px",
            },
            dialogCloseButtom: {
                top: "12px",
                right: "14px",
            },
            confirmDialog: {
                maxWidth: "600px",
            },
            titleStyle: {
                fontSize: "16px",
                fontWeight: "bold",
                marginLeft: "0px",
                marginRight: "0px",
                padding: "12px 24px",
                borderBottom: "1px solid lightgray",
            },
            bodyStyle: {
                borderBottom: "none",
                borderTop: "none",
            },
        }),
        []
    );

    const handleClose = useCallback(() => {
        onClose(null);
    }, [onClose]);
    const handleSelect = useCallback(() => {
        const cartUpdate = formatForCart(selectedCompany, hasDiscountPreference);
        fetch(`/working/update/${orderId}`, {
            method: "POST",
            body: JSON.stringify(cartUpdate),
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(format("Oops! Something went wrong. Please refresh and try again."));
                }
            })
            .then((data: IOrder) => {
                return onSyncFromCart(data, { syncToCart: true });
            })
            .then(() => {
                onConfirm(selectedCompany);
            })
            .catch((error: Error) => {
                onSnackbarAdd(error);
            });
    }, [hasDiscountPreference, orderId, selectedCompany, onSyncFromCart, onConfirm, onSnackbarAdd]);

    useEffect(() => {
        setSelectedCompany(scope.company ? new CompanyRecord(scope.company) : null);
    }, [scope.company]);

    const classes = useStyles({});

    return (
        <ConfirmDialog
            open={open}
            title={format("Select Company")}
            cancelLabel={format("Cancel")}
            onCancel={handleClose}
            confirmLabel={format("Select")}
            onConfirm={handleSelect}
            disabledConfirm={!selectedCompany}
            actionsContainerStyle={styles.actionsStyle}
            closeButtonStyle={styles.dialogCloseButtom}
            contentStyle={styles.confirmDialog}
            titleStyle={styles.titleStyle}
            bodyStyle={styles.bodyStyle}
            dataTestConfirm={DATATEST.GALLERY.COMPANY_SELECTION_CONFIRM}
            dataTestCancel={DATATEST.GALLERY.COMPANY_SELECTION_CANCEL}
        >
            <Form className={classes.content}>
                <p className={classes.description}>
                    {format(
                        "Check to see if there are company-level promotions for the company for which you are shopping."
                    )}
                </p>
                <h4 className={classes.subtitle}>{format("Company:")}</h4>
                <div className={classes.companySelect}>
                    <CompanySelect
                        open={!!selectedCompany}
                        useSearchIcon={true}
                        disableLabel={true}
                        dataTest={DATATEST.GALLERY.COMPANY_SELECTION_INPUT}
                        selectedCompany={selectedCompany}
                        onSelectedCompanyChange={setSelectedCompany}
                        onFetchStartOrderCompanies={onFetchStartOrderCompanies}
                        fetchingCompanies={fetchingCompanies}
                        companies={companies}
                        companyIds={companyIds}
                        hasMoreCompanies={hasMoreCompanies}
                        user={user}
                        scope={scope}
                    />
                </div>
            </Form>
        </ConfirmDialog>
    );
};

interface ICart {
    __uninitiated_order: boolean;
    retailer?: { ref?: string; retailer_name?: string; buyer?: string; buyer_name?: string };
    discount?: number;
    additional_percentage?: number;
    currency_code?: string;
}

const formatForCart = (company: TFDMCompanyRecord | undefined, hasDiscountPreference: boolean) => {
    const cart: ICart = { __uninitiated_order: false };
    if (!company) {
        return cart;
    }

    const { __default_discount: defaultDiscount, __default_surcharge: defaultSurcharge } = company.__settings;
    cart.retailer = retailerInfo(company);
    cart.currency_code = company.__currency_codes?.get(0);
    if (hasDiscountPreference && defaultDiscount) {
        cart.discount = defaultDiscount;
    }

    if (defaultSurcharge) {
        cart.additional_percentage = defaultSurcharge;
    }

    return cart;
};

export const retailerInfo = (company: TFDMCompanyRecord) => ({
    ref: company._id,
    retailer_name: company.__name,
    buyer: company.__buyers?.get(0)?.__ref,
    buyer_name: company.__buyers?.get(0)?.__name,
});

const useStyles = makeStyles({
    content: {
        color: rawTheme.palette.textColor,
        fontSize: "14px",
        marginBottom: "40px",
    },
    subtitle: {
        fontWeight: "bold",
        margin: "18px 5px 0 0",
    },
    description: {
        marginTop: "10px",
    },
    companySelect: {
        width: "100%",
        maxWidth: "350px",
    },
});
